import { SignupAttribution } from '../types';

const STORAGE_KEY = 'jsonpad.attribution';
const MAX_LENGTH = 500;
const OWN_HOST = 'jsonpad.io';

const UTM_PARAMS: [string, keyof SignupAttribution][] = [
  ['utm_source', 'utmSource'],
  ['utm_medium', 'utmMedium'],
  ['utm_campaign', 'utmCampaign'],
  ['utm_term', 'utmTerm'],
  ['utm_content', 'utmContent'],
];

const KEYS: (keyof SignupAttribution)[] = [
  'referrer',
  'landingPage',
  ...UTM_PARAMS.map(([, key]) => key),
  'firstSeenAt',
];

function hasCampaign(attribution: SignupAttribution): boolean {
  return UTM_PARAMS.some(([, key]) => !!attribution[key]);
}

/**
 * Where the visitor came from, without the query string - a referring URL can
 * carry search terms or session ids that we have no business keeping
 */
function externalReferrer(): string | undefined {
  if (!document.referrer) {
    return undefined;
  }

  let url: URL;
  try {
    url = new URL(document.referrer);
  } catch (error) {
    return undefined;
  }

  if (
    url.hostname === window.location.hostname ||
    url.hostname === OWN_HOST ||
    url.hostname.endsWith(`.${OWN_HOST}`)
  ) {
    return undefined;
  }

  return url.origin + url.pathname;
}

/**
 * Read the stored attribution, keeping only what the API will accept
 */
export function readAttribution(): SignupAttribution | null {
  let stored: unknown;
  try {
    stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
  } catch (error) {
    return null;
  }

  if (!stored || typeof stored !== 'object' || Array.isArray(stored)) {
    return null;
  }

  const attribution: SignupAttribution = {};
  for (const key of KEYS) {
    const value = (stored as Record<string, unknown>)[key];

    if (typeof value === 'string' && value) {
      attribution[key] = value.slice(0, MAX_LENGTH);
    }
  }

  return attribution;
}

export function clearAttribution(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {}
}

/**
 * Remember how this visitor first reached the site, so it can be sent once
 * when they sign up
 *
 * First touch wins, with one exception: a later visit from a tagged campaign
 * link replaces an entry that has no campaign, because a known campaign says
 * more than a bare referrer does.
 */
export function captureAttribution(): void {
  try {
    const params = new URLSearchParams(window.location.search);

    const current: SignupAttribution = {};
    for (const [param, key] of UTM_PARAMS) {
      const value = params.get(param);

      if (value) {
        current[key] = value.slice(0, MAX_LENGTH);
      }
    }

    const stored = readAttribution();
    if (stored && (hasCampaign(stored) || !hasCampaign(current))) {
      return;
    }

    const referrer = externalReferrer();
    if (referrer) {
      current.referrer = referrer.slice(0, MAX_LENGTH);
    }

    current.landingPage = window.location.pathname.slice(0, MAX_LENGTH);
    current.firstSeenAt = new Date().toISOString();

    localStorage.setItem(STORAGE_KEY, JSON.stringify(current));
  } catch (error) {}
}

/**
 * The one-word answer to "where did this user come from?"
 */
export function attributionSource(
  attribution?: SignupAttribution | null
): string {
  if (attribution?.utmSource) {
    return attribution.utmSource;
  }

  if (attribution?.referrer) {
    try {
      return new URL(attribution.referrer).hostname;
    } catch (error) {}
  }

  return 'direct';
}
